import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, MinusCircle, Sparkles, ShieldCheck, MapPin, Languages as LangIcon, Heart, MessageCircleHeart, Loader2, Copy } from "lucide-react";

function buildFactors(me, them) {
  const f = [];
  const add = (label, ok, detail, icon) => f.push({ label, ok, detail, icon });

  if (me?.sect && them?.sect) add("School of thought", me.sect === them.sect, them.sect, ShieldCheck);
  if (me?.prayer_habit && them?.prayer_habit) add("Prayer practice", me.prayer_habit === them.prayer_habit, them.prayer_habit, Heart);
  if (me?.marital_status && them?.marital_status) add("Marital background", me.marital_status === them.marital_status, them.marital_status, CheckCircle2);

  const sameCountry = !!(me?.country && them?.country && me.country === them.country);
  if (them?.country || them?.city) add("Location", sameCountry, sameCountry ? them.country : [them.city, them.country].filter(Boolean).join(", "), MapPin);
  if (typeof them?.relocation === "boolean") add("Open to relocation", !!them.relocation, them.relocation ? "Open" : "Not now", MapPin);

  const langs = (me?.languages || []).filter((l) => (them?.languages || []).includes(l));
  if ((me?.languages || []).length && (them?.languages || []).length) add("Shared languages", langs.length > 0, langs.join(", ") || "None in common", LangIcon);

  const ints = (me?.interests || []).filter((l) => (them?.interests || []).includes(l));
  if ((me?.interests || []).length && (them?.interests || []).length) add("Shared interests", ints.length > 0, ints.slice(0, 4).join(", ") || "None in common", Sparkles);

  return f.sort((a, b) => Number(b.ok) - Number(a.ok));
}

function buildPrompt(them, factors) {
  const shared = factors.filter((f) => f.ok).map((f) => `${f.label}: ${f.detail}`).join("; ");
  const diff = factors.filter((f) => !f.ok).map((f) => f.label).join(", ");
  const name = them?.firstName || them?.name || "them";
  return `For an Islamic marriage-matching app, suggest 4 thoughtful, respectful questions I could ask ${name} to get to know them for marriage, keeping in mind Islamic etiquette and involving the wali. We share: ${shared || "some values"}. We may differ on: ${diff || "a few areas"}. Return ONLY a numbered list of 4 short questions, no preamble.`;
}

export function CompatibilityBreakdown({ me, them, className = "", showInsights = true }) {
  const factors = buildFactors(me, them);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);

  if (factors.length === 0) return null;
  const pct = typeof them?.match_percent === "number" ? them.match_percent : null;
  const shared = factors.filter((f) => f.ok).length;

  const suggest = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/coach", { message: buildPrompt(them, factors) });
      const reply = data.reply || data.message || data.text || "";
      const lines = reply.split("\n").map((l) => l.replace(/^\s*[\d.)*-]+\s*/, "").trim()).filter((l) => l.length > 4);
      setQuestions(lines.slice(0, 4));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const copy = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Question copied"); } catch {} };

  return (
    <div className={`rounded-3xl border border-emerald-900/10 bg-white p-6 shadow-sm ${className}`} data-testid="compatibility-breakdown">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 font-serif text-xl font-semibold text-emerald-900"><Sparkles className="h-5 w-5 text-amber-600" /> Why you match</h3>
          <p className="mt-0.5 text-sm text-slate-500">You share {shared} of {factors.length} key areas</p>
        </div>
        {pct !== null && (
          <div className="relative flex h-16 w-16 shrink-0 items-center justify-center">
            <svg viewBox="0 0 36 36" className="h-16 w-16 -rotate-90">
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="#E7F3EE" strokeWidth="4" />
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="#0E6E5C" strokeWidth="4" strokeLinecap="round" strokeDasharray={`${(pct / 100) * 97.4} 97.4`} />
            </svg>
            <span className="absolute text-sm font-bold text-emerald-800">{pct}%</span>
          </div>
        )}
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {factors.map((f) => {
          const Icon = f.icon;
          return (
            <div key={f.label} className={`flex items-start gap-2.5 rounded-xl p-3 ${f.ok ? "bg-mint/50" : "bg-slate-50"}`}>
              {f.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <MinusCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />}
              <div className="min-w-0">
                <p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700"><Icon className="h-3 w-3" /> {f.label}</p>
                <p className="truncate text-sm font-medium text-slate-800">{f.detail || "—"}</p>
              </div>
            </div>
          );
        })}
      </div>

      {showInsights && (
        <div className="mt-4 border-t border-emerald-900/10 pt-4">
          <button onClick={suggest} disabled={loading} data-testid="insights-suggest-btn" className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-700 to-emerald-900 px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-95 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircleHeart className="h-4 w-4 text-amber-300" />} {questions.length ? "Suggest more questions" : "Suggest questions to ask (AI Coach)"}
          </button>
          {questions.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="insights-questions">
              {questions.map((q, i) => (
                <button key={i} onClick={() => copy(q)} data-testid={`insight-question-${i}`} className="group flex w-full items-start gap-2 rounded-xl border border-emerald-900/10 bg-mint/40 p-3 text-left transition-colors hover:bg-mint">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-[11px] font-bold text-white">{i + 1}</span>
                  <span className="flex-1 text-sm leading-relaxed text-slate-700">{q}</span>
                  <Copy className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700 opacity-60 group-hover:opacity-100" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
